import React, { Fragment, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getEnforcementOverview } from "./api";
import { getBudgetDimensions } from "../cost-allocation/api/costModelApi";
import { deriveSummary } from "./overviewDerive";
import EnforcementVerdict from "./components/EnforcementVerdict";
import GovernanceKpis from "./components/GovernanceKpis";
import BudgetsTable from "./components/BudgetsTable";
import EnforcementBudgetDrawer from "./components/EnforcementBudgetDrawer";
import PeriodCaption from "../cost-control/PeriodCaption";
import FreshnessLabel from "../../components/ui-components/FreshnessLabel";
import Tooltip from "../../components/form-components/Tooltip.jsx";
import { HeaderPortal } from "../../components/ui-components/HeaderSlot";

// Section wrapper: supports the verdict/KPI drill via `innerRef` + `highlighted`
// (scroll-to + transient outline ring that never shifts surrounding layout).
const Section = ({ innerRef, highlighted, children }) => (
  <section
    ref={innerRef}
    style={{
      scrollMarginTop: 16,
      borderRadius: 16,
      outline: highlighted ? "2px solid hsl(var(--primary))" : "2px solid transparent",
      outlineOffset: 6,
      transition: "outline-color 0.3s ease",
    }}
  >
    {children}
  </section>
);

export default function BudgetEnforcementPage() {
  const { data, isLoading, error } = useQuery(["budget-enforcement-overview"], getEnforcementOverview);
  const navigate = useNavigate();
  const ov = data || {};
  const summary = deriveSummary(ov);

  // Perspective filter chips come from the SAME /budget-dimensions source the
  // /cost-allocation/hierarchy page reads (shared query cache), so the perspectives read
  // identically — Organisation / Financial / Technical / Use-case. Organisation leads.
  const { data: dimsData } = useQuery(["budget-dimensions"], getBudgetDimensions);
  const isOrgPerspective = (o) =>
    o.value === "org" || String(o.label ?? "").toLowerCase().startsWith("organi");
  const scopeOptions = [
    { value: "all", label: "All" },
    ...(dimsData ?? [])
      .map((d) => ({ value: d.code ?? d.Code, label: d.name ?? d.Name }))
      .filter((o) => o.value)
      .sort((a, b) => (isOrgPerspective(a) ? 0 : 1) - (isOrgPerspective(b) ? 0 : 1)),
  ];

  const [scopeLevel, setScopeLevel] = useState("all");
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [highlight, setHighlight] = useState(null);
  const [selected, setSelected] = useState(null); // the budget row open in the drawer

  const tableRef = useRef(null);

  const flash = (ref, key) => {
    if (ref.current) ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    setHighlight(key);
    window.setTimeout(() => setHighlight((cur) => (cur === key ? null : cur)), 2200);
  };

  // Period context (mirrors the Control header) + portal target.
  const now = new Date();
  const asOf = now.toLocaleString("en-IE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  const period = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const monthLabel = now.toLocaleString("en-IE", { month: "long", year: "numeric" });

  // Sign-offs are auto-created on a hard breach (policy approval-gate step), so the queue is
  // exactly the pending approvals. pendingBudgetIds/approvalByBudget drive the per-row "Awaiting"
  // signal and the "Review" deep-link.
  const pendingApprovals = ov.pending_approvals || [];
  const pendingBudgetIds = new Set(pendingApprovals.map((a) => a.budget_id));
  const approvalByBudget = new Map(pendingApprovals.map((a) => [a.budget_id, a.approval_request_id]));
  const signoffCount = pendingApprovals.length;

  // Verdict / KPI drill targets. The sign-off queue no longer has a card on this page, so its
  // drill goes to the approvals workspace itself rather than to a summary of it.
  const drill = (target) => {
    if (target === "signoffs") navigate("/approvals");
    else if (target === "budgets") {
      setAttentionOnly(true);
      flash(tableRef, "table");
    }
  };

  // Budget-level actions (from table rows and the drawer).
  const handleBudgetAction = (kind, row) => {
    if (kind === "open_budget") navigate(`/budgets/${row.budget_id}`);
    else if (kind === "attach_policy") navigate("/budget-enforcement/policies");
    else if (kind === "review") {
      const id = approvalByBudget.get(row.budget_id);
      if (id != null) navigate(`/approvals/${id}`);
    }
  };

  return (
    <Fragment>
      <div className="px-8 py-6">
        {error && <div className="text-sm text-danger">Failed to load enforcement overview.</div>}

        {!isLoading && !error && (
          <>
            {/* Current period + as-of context lives on row 2 (matching Control / the
                cost analyzer): the period label, a muted caption, then freshness. */}
            <HeaderPortal slot="row2-right">
              <div className="flex items-center gap-2 whitespace-nowrap">
                <span className="text-[13px] font-medium text-text-2">{monthLabel}</span>
                <span aria-hidden style={{ width: 1, height: 16 }} className="bg-card-bd" />
                <PeriodCaption period={period} />
                <span aria-hidden className="mx-0.5 text-[#C6CDD8] select-none">·</span>
                <FreshnessLabel>as of {asOf}</FreshnessLabel>
              </div>
            </HeaderPortal>

            {/* Posture verdict (governance + workflow, not spend) */}
            <div className="mb-4">
              <EnforcementVerdict summary={summary} onReview={drill} />
            </div>

            {/* Governance KPI strip */}
            <div className="mb-5">
              <GovernanceKpis
                governance={summary.governance}
                signoffCount={signoffCount}
                onAttachPolicy={() => navigate("/budget-enforcement/policies")}
                onReviewQueue={() => navigate("/approvals")}
              />
            </div>

            {/* The budgets table is the screen. The approval-queue and policies cards used to sit
                in a side rail; both are reachable in full at /approvals and
                /budget-enforcement/policies, and their headline numbers already live in the KPI
                strip above, so the table gets the whole width instead of a summary of a summary. */}
            <Section innerRef={tableRef} highlighted={highlight === "table"}>
              <BudgetsTable
                rows={ov.scope_status || []}
                policies={ov.policies || []}
                scope={scopeLevel}
                onScopeChange={setScopeLevel}
                scopeOptions={scopeOptions}
                attentionOnly={attentionOnly}
                onAttentionChange={setAttentionOnly}
                pendingBudgetIds={pendingBudgetIds}
                onSelect={setSelected}
                onAction={handleBudgetAction}
              />
            </Section>
          </>
        )}
      </div>

      {/* Per-budget drawer: reused Control burn-down chart + guardrail ladder + sign-off. */}
      <EnforcementBudgetDrawer
        selected={selected}
        policies={ov.policies || []}
        period={period}
        approvalRequestId={selected ? approvalByBudget.get(selected.budget_id) ?? null : null}
        onClose={() => setSelected(null)}
        onAction={handleBudgetAction}
      />

      {/* Shared react-tooltip instance for data-tooltip-id="instantTooltip" anchors (owner cells). */}
      <Tooltip />
    </Fragment>
  );
}
